import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Flame, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Common Preset Package Addons
const PRESET_PACKAGE_ADDONS = [ "Raw Files", "Reels", "Calender", "Mini Book", "Frame","Pendrive Box"];
const EVENT_ADDON_SUGGESTIONS = ["Livestreaming", "Live Printing", "Photo Sharing", "Photo Booth", "Drone", "Same Day Edit"];

const emptyEvent = () => ({
    id: undefined,
    event_name: "",
    event_date: "",
    needed_photographers: 1,
    needed_videographers: 0,
    addons: [],
    links: [],
    assignments: [],
});

const emptyClient = () => ({
    name: "",
    expected_delivery_date: "",
    is_fast_track: false,
    current_stage: "Enquiry",
    contact_email: "",
    contact_phone: "",
    package: { album_type: "Lustre", album_pages: 30, addons: [], total_committed_amount: 0 },
    events: [emptyEvent()],
});

export default function ClientModal({ open, onClose, onSaved, initial }) {
    const [tab, setTab] = useState("client");
    const [form, setForm] = useState(emptyClient());
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setForm(initial ? { ...emptyClient(), ...initial, events: initial.events?.length ? initial.events : [emptyEvent()] } : emptyClient());
            setTab("client");
        }
    }, [open, initial]);

    const setField = (path, value) => {
        setForm((f) => {
            const clone = JSON.parse(JSON.stringify(f));
            const parts = path.split(".");
            let ref = clone;
            for (let i = 0; i < parts.length - 1; i++) ref = ref[parts[i]];
            ref[parts[parts.length - 1]] = value;
            return clone;
        });
    };

    // Handler specifically for Album Type changes
    const handleAlbumTypeChange = (value) => {
        setForm((f) => {
            const isNoAlbum = value === "No Album";
            return {
                ...f,
                package: {
                    ...f.package,
                    album_type: value,
                    // Automatically set album_pages to 0 if 'No Album' is selected
                    album_pages: isNoAlbum ? 0 : (f.package.album_pages || 30),
                },
            };
        });
    };

    // --- Package Addon Helper Functions ---
    const togglePackageAddon = (addon) => {
        setForm((f) => {
            const list = f.package?.addons || [];
            const has = list.some((a) => a.toLowerCase() === addon.toLowerCase());
            const addons = has
                ? list.filter((a) => a.toLowerCase() !== addon.toLowerCase())
                : [...list, addon];
            return { ...f, package: { ...f.package, addons } };
        });
    };

    const addCustomPackageAddon = (label) => {
        const clean = (label || "").trim();
        if (!clean) return;
        setForm((f) => {
            const list = f.package?.addons || [];
            if (list.some((a) => a.toLowerCase() === clean.toLowerCase())) return f;
            return { ...f, package: { ...f.package, addons: [...list, clean] } };
        });
    };

    const removePackageAddon = (addon) => {
        setForm((f) => ({
            ...f,
            package: {
                ...f.package,
                addons: (f.package?.addons || []).filter((a) => a !== addon)
            }
        }));
    };

    const addEvent = () => setForm((f) => ({ ...f, events: [...f.events, emptyEvent()] }));
    const removeEvent = (idx) =>
        setForm((f) => ({ ...f, events: f.events.filter((_, i) => i !== idx) }));

    const updateEvent = (idx, field, value) => {
        setForm((f) => {
            const events = f.events.map((ev, i) => (i === idx ? { ...ev, [field]: value } : ev));
            return { ...f, events };
        });
    };

    const addEventAddon = (idx, label) => {
        const clean = (label || "").trim();
        if (!clean) return;
        setForm((f) => {
            const events = f.events.map((ev, i) => {
                if (i !== idx) return ev;
                const list = ev.addons || [];
                if (list.some((a) => a.toLowerCase() === clean.toLowerCase())) return ev;
                return { ...ev, addons: [...list, clean] };
            });
            return { ...f, events };
        });
    };

    const removeEventAddon = (idx, addon) => {
        setForm((f) => {
            const events = f.events.map((ev, i) =>
                i === idx ? { ...ev, addons: (ev.addons || []).filter((a) => a !== addon) } : ev
            );
            return { ...f, events };
        });
    };

    const validate = () => {
        if (!form.name.trim()) return "Client name is required";
        if (!form.expected_delivery_date) return "Expected delivery date is required";
        for (const e of form.events) {
            if (!e.event_name.trim()) return "Each event needs a name";
            if (!e.event_date) return "Each event needs a date";
        }
        return null;
    };

    const submit = async () => {
        const err = validate();
        if (err) {
            toast.error(err);
            return;
        }
        setSaving(true);
        try {
            const payload = { ...form };
            payload.package.total_committed_amount = Number(payload.package.total_committed_amount) || 0;
            payload.package.album_pages = Number(payload.package.album_pages) || 0;
            payload.events = payload.events.map((e) => ({
                ...e,
                needed_photographers: Number(e.needed_photographers) || 0,
                needed_videographers: Number(e.needed_videographers) || 0,
                addons: e.addons || [],
                links: e.links || [],
                assignments: e.assignments || [],
            }));
            if (initial?.id) {
                await api.put(`/clients/${initial.id}`, payload);
                toast.success("Client updated");
            } else {
                await api.post("/clients", payload);
                toast.success("Client created");
            }
            onSaved && onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    const isNoAlbum = form.package.album_type === "No Album";

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="w-[95vw] sm:max-w-3xl p-0 overflow-hidden rounded-lg max-h-[90vh] flex flex-col" data-testid="client-modal">
                <DialogHeader className="px-4 sm:px-6 pt-5 pb-4 border-b border-gray-100 shrink-0">
                    <DialogTitle className="font-serif text-xl sm:text-2xl text-gray-900">
                        {initial?.id ? "Edit Client" : "New Client Onboarding"}
                    </DialogTitle>
                    <DialogDescription className="text-xs sm:text-sm text-gray-500 mt-1">
                        Set up the client, their package and all scheduled events.
                    </DialogDescription>
                </DialogHeader>

                <Tabs value={tab} onValueChange={setTab} className="w-full flex-1 flex flex-col min-h-0">
                    <div className="px-4 sm:px-6 pt-3 shrink-0 overflow-x-auto no-scrollbar border-b border-gray-100">
                        <TabsList className="bg-transparent border-b border-gray-200 rounded-none p-0 w-full justify-start gap-4 sm:gap-6 h-auto whitespace-nowrap flex-nowrap">
                            <TabsTrigger
                                value="client"
                                data-testid="tab-client-info"
                                className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 pb-2.5 px-1 text-xs sm:text-sm bg-transparent data-[state=active]:bg-transparent"
                            >
                                Client Info
                            </TabsTrigger>
                            <TabsTrigger
                                value="package"
                                data-testid="tab-package-info"
                                className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 pb-2.5 px-1 text-xs sm:text-sm bg-transparent data-[state=active]:bg-transparent"
                            >
                                Package
                            </TabsTrigger>
                            <TabsTrigger
                                value="events"
                                data-testid="tab-events"
                                className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 pb-2.5 px-1 text-xs sm:text-sm bg-transparent data-[state=active]:bg-transparent"
                            >
                                Events ({form.events.length})
                            </TabsTrigger>
                        </TabsList>
                    </div>

                    <div className="px-4 sm:px-6 py-4 sm:py-6 flex-1 overflow-y-auto">
                        {/* CLIENT INFO */}
                        <TabsContent value="client" className="mt-0 space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="sm:col-span-2">
                                    <Label className="text-xs sm:text-sm">Client name</Label>
                                    <Input
                                        value={form.name}
                                        onChange={(e) => setField("name", e.target.value)}
                                        placeholder="Sarah & Mike Wedding"
                                        data-testid="client-name-input"
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs sm:text-sm">Expected delivery date</Label>
                                    <Input
                                        type="date"
                                        value={form.expected_delivery_date?.slice(0, 10) || ""}
                                        onChange={(e) => setField("expected_delivery_date", e.target.value)}
                                        data-testid="client-delivery-input"
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs sm:text-sm">Current stage</Label>
                                    <Select
                                        value={form.current_stage}
                                        onValueChange={(v) => setField("current_stage", v)}
                                    >
                                        <SelectTrigger className="mt-1" data-testid="client-stage-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Enquiry">Enquiry</SelectItem>
                                            <SelectItem value="InProgress">In Progress</SelectItem>
                                            <SelectItem value="PostProduction">Post-Production</SelectItem>
                                            <SelectItem value="Completed">Completed</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs sm:text-sm">Contact email</Label>
                                    <Input
                                        type="email"
                                        value={form.contact_email || ""}
                                        onChange={(e) => setField("contact_email", e.target.value)}
                                        data-testid="client-email-input"
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs sm:text-sm">Contact phone</Label>
                                    <Input
                                        value={form.contact_phone || ""}
                                        onChange={(e) => setField("contact_phone", e.target.value)}
                                        data-testid="client-phone-input"
                                        className="mt-1"
                                    />
                                </div>
                                <div className="sm:col-span-2 flex items-center justify-between border border-amber-200 bg-amber-50 rounded-md p-3 sm:p-4 gap-3">
                                    <div className="flex items-start gap-2.5">
                                        <Flame className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                                        <div>
                                            <div className="text-xs sm:text-sm font-medium text-amber-900">Fast Track Client</div>
                                            <div className="text-[11px] sm:text-xs text-amber-800/80 mt-0.5">
                                                Prioritise post-production and delivery for this project.
                                            </div>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={form.is_fast_track}
                                        onCheckedChange={(v) => setField("is_fast_track", v)}
                                        data-testid="client-fast-track-switch"
                                    />
                                </div>
                            </div>
                        </TabsContent>

                        {/* PACKAGE */}
                        <TabsContent value="package" className="mt-0 space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs sm:text-sm">Album type</Label>
                                    <Select
                                        value={form.package.album_type}
                                        onValueChange={handleAlbumTypeChange}
                                    >
                                        <SelectTrigger className="mt-1" data-testid="package-album-type-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Lustre">Lustre</SelectItem>
                                            <SelectItem value="Glossy">Glossy</SelectItem>
                                            <SelectItem value="Customized/Mixed Album">Customized/Mixed Album</SelectItem>
                                            <SelectItem value="No Album">No Album</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className={`text-xs sm:text-sm ${isNoAlbum ? "text-gray-400" : ""}`}>
                                        Album pages
                                    </Label>
                                    <Input
                                        type="number"
                                        min={0}
                                        value={isNoAlbum ? 0 : form.package.album_pages}
                                        disabled={isNoAlbum}
                                        onChange={(e) => setField("package.album_pages", e.target.value)}
                                        data-testid="package-pages-input"
                                        className="mt-1 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <Label className="text-xs sm:text-sm">Total committed amount (₹)</Label>
                                    <Input
                                        type="number"
                                        min={0}
                                        step="0.01"
                                        value={form.package.total_committed_amount}
                                        onChange={(e) => setField("package.total_committed_amount", e.target.value)}
                                        data-testid="package-total-input"
                                        className="mt-1 tabular-nums"
                                    />
                                </div>

                                {/* HYBRID PACKAGE ADDONS EDITOR (PRESETS + CUSTOM INPUT) */}
                                <div className="sm:col-span-2">
                                    <PackageAddonEditor
                                        selectedAddons={form.package.addons || []}
                                        onToggle={togglePackageAddon}
                                        onAddCustom={addCustomPackageAddon}
                                        onRemove={removePackageAddon}
                                    />
                                </div>
                            </div>
                        </TabsContent>

                        {/* EVENTS */}
                        <TabsContent value="events" className="mt-0 space-y-4">
                            {form.events.map((ev, idx) => (
                                <div key={idx} className="border border-gray-200 bg-gray-50/70 rounded-md p-3.5 sm:p-4" data-testid={`event-block-${idx}`}>
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-gray-500">
                                            Event {idx + 1}
                                        </div>
                                        {form.events.length > 1 && (
                                            <button
                                                onClick={() => removeEvent(idx)}
                                                data-testid={`remove-event-${idx}`}
                                                className="text-gray-400 hover:text-red-600 transition-colors p-1"
                                                title="Remove Event"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div className="sm:col-span-2">
                                            <Label className="text-xs sm:text-sm">Event name</Label>
                                            <Input
                                                value={ev.event_name}
                                                onChange={(e) => updateEvent(idx, "event_name", e.target.value)}
                                                placeholder="Engagement Shoot"
                                                className="mt-1 bg-white"
                                                data-testid={`event-name-${idx}`}
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs sm:text-sm">Date</Label>
                                            <Input
                                                type="date"
                                                value={ev.event_date?.slice(0, 10) || ""}
                                                onChange={(e) => updateEvent(idx, "event_date", e.target.value)}
                                                className="mt-1 bg-white"
                                                data-testid={`event-date-${idx}`}
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 sm:gap-3">
                                            <div>
                                                <Label className="text-xs sm:text-sm">Photographers</Label>
                                                <Input
                                                    type="number"
                                                    min={0}
                                                    value={ev.needed_photographers}
                                                    onChange={(e) => updateEvent(idx, "needed_photographers", e.target.value)}
                                                    className="mt-1 bg-white tabular-nums"
                                                    data-testid={`event-photogs-${idx}`}
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-xs sm:text-sm">Videographers</Label>
                                                <Input
                                                    type="number"
                                                    min={0}
                                                    value={ev.needed_videographers}
                                                    onChange={(e) => updateEvent(idx, "needed_videographers", e.target.value)}
                                                    className="mt-1 bg-white tabular-nums"
                                                    data-testid={`event-videogs-${idx}`}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <EventAddonEditor
                                        idx={idx}
                                        addons={ev.addons || []}
                                        onAdd={(val) => addEventAddon(idx, val)}
                                        onRemove={(val) => removeEventAddon(idx, val)}
                                    />
                                </div>
                            ))}
                            <Button
                                variant="outline"
                                onClick={addEvent}
                                className="w-full border-dashed text-xs sm:text-sm"
                                data-testid="add-event-button"
                            >
                                <Plus className="w-4 h-4 mr-1.5" /> Add another event
                            </Button>
                        </TabsContent>
                    </div>
                </Tabs>

                <DialogFooter className="border-t border-gray-100 px-4 sm:px-6 py-3.5 bg-gray-50 flex-row justify-end gap-2 shrink-0">
                    <Button variant="outline" onClick={onClose} size="sm" data-testid="cancel-client-modal">
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={saving}
                        size="sm"
                        className="bg-blue-500 hover:bg-blue-600 text-white"
                        data-testid="save-client-button"
                    >
                        {saving ? "Saving…" : initial?.id ? "Save changes" : "Create client"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Package Addon Hybrid Component (Presets + Custom Input)
function PackageAddonEditor({ selectedAddons, onToggle, onAddCustom, onRemove }) {
    const [customVal, setCustomVal] = useState("");

    const handleAddCustom = () => {
        if (!customVal.trim()) return;
        onAddCustom(customVal);
        setCustomVal("");
    };

    // Filter out custom items that are NOT in the standard presets
    const customItems = selectedAddons.filter(
        (addon) => !PRESET_PACKAGE_ADDONS.some((preset) => preset.toLowerCase() === addon.toLowerCase())
    );

    return (
        <div>
            <Label className="text-xs sm:text-sm font-medium text-gray-800">Package Addons</Label>
            
            {/* Quick-Select Preset Pills */}
            <div className="mt-2 flex flex-wrap gap-2">
                {PRESET_PACKAGE_ADDONS.map((preset) => {
                    const isChecked = selectedAddons.some(
                        (a) => a.toLowerCase() === preset.toLowerCase()
                    );
                    return (
                        <button
                            key={preset}
                            type="button"
                            onClick={() => onToggle(preset)}
                            data-testid={`preset-addon-${preset.replace(/\s+/g, "-").toLowerCase()}`}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs sm:text-sm border transition-colors ${
                                isChecked
                                    ? "bg-blue-50 border-blue-500 text-blue-700 font-medium"
                                    : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                            }`}
                        >
                            <Checkbox checked={isChecked} className="pointer-events-none h-3.5 w-3.5" />
                            {preset}
                        </button>
                    );
                })}
            </div>

            {/* Render Custom-Added Badges */}
            {customItems.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5 items-center">
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mr-1">Custom:</span>
                    {customItems.map((item) => (
                        <span
                            key={item}
                            data-testid={`custom-addon-${item.replace(/\s+/g, "-").toLowerCase()}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                        >
                            {item}
                            <button
                                type="button"
                                onClick={() => onRemove(item)}
                                className="text-blue-500 hover:text-red-600 p-0.5 transition-colors"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}

            {/* Custom Input Field */}
            <div className="mt-3 flex gap-2">
                <Input
                    value={customVal}
                    onChange={(e) => setCustomVal(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddCustom();
                        }
                    }}
                    placeholder="Or type a custom addon (e.g. Canvas Frame) and press Enter"
                    className="bg-white flex-1 text-xs sm:text-sm"
                    data-testid="package-addon-custom-input"
                />
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddCustom}
                    disabled={!customVal.trim()}
                    data-testid="package-addon-add-custom-button"
                    className="shrink-0"
                >
                    <Plus className="w-3.5 h-3.5 sm:mr-1" />
                    <span className="hidden sm:inline">Add</span>
                </Button>
            </div>
        </div>
    );
}

// Event Addon Custom Text Editor
function EventAddonEditor({ idx, addons, onAdd, onRemove }) {
    const [value, setValue] = useState("");
    const commit = () => {
        if (!value.trim()) return;
        onAdd(value);
        setValue("");
    };

    return (
        <div className="mt-4 pt-3 border-t border-dashed border-gray-200">
            <Label className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-500 font-semibold">
                Event Addons
            </Label>
            <div className="mt-2 flex flex-wrap gap-1.5">
                {addons.length === 0 && (
                    <span className="text-xs text-gray-400">
                        Add services like livestreaming, live printing, photo sharing…
                    </span>
                )}
                {addons.map((a) => (
                    <span
                        key={a}
                        data-testid={`event-${idx}-addon-${a.replace(/\s+/g, "-").toLowerCase()}`}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200"
                    >
                        {a}
                        <button
                            type="button"
                            onClick={() => onRemove(a)}
                            className="text-blue-500 hover:text-red-600 p-0.5"
                            data-testid={`event-${idx}-remove-addon-${a.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                ))}
            </div>
            <div className="mt-2.5 flex gap-2">
                <Input
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            commit();
                        }
                    }}
                    placeholder="Type addon (e.g. livestreaming)"
                    className="bg-white flex-1 text-xs sm:text-sm"
                    data-testid={`event-addon-input-${idx}`}
                />
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={commit}
                    disabled={!value.trim()}
                    data-testid={`event-addon-add-button-${idx}`}
                    className="shrink-0"
                >
                    <Plus className="w-3.5 h-3.5 sm:mr-1" />
                    <span className="hidden sm:inline">Add</span>
                </Button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
                {EVENT_ADDON_SUGGESTIONS.filter((s) => !addons.some((a) => a.toLowerCase() === s.toLowerCase())).map((s) => (
                    <button
                        type="button"
                        key={s}
                        onClick={() => onAdd(s)}
                        className="text-[11px] text-gray-500 hover:text-blue-600 hover:underline py-0.5"
                        data-testid={`event-addon-suggest-${idx}-${s.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                        + {s}
                    </button>
                ))}
            </div>
        </div>
    );
}